import '@nike/eds/dist/index.css'
import {toRelativeUrl} from '@okta/okta-auth-js'
import {LoginCallback, Security} from '@okta/okta-react'
import React, {useCallback, useContext, useEffect} from 'react'
import {Navigate, Route, Routes, useNavigate, useSearchParams} from 'react-router-dom'
import {oktaAuth} from './config/OktaConfig'
import {LoginError} from './login-error/LoginError'
import {RequiredAuth} from './shared/AuthenticatedRoute'
import {createBrowserHistory} from 'history'
import {MasterData} from "./pages/MasterData";
import {Exceptions} from "./pages/Exceptions";
import {Dashboard} from "./pages/Dashboard";
import {EquipmentVehicles} from "./pages/EquipmentVehicles";
import {ConsignmentPage} from "./pages/Consignment";
import {SearchPage} from "./pages/Search";
import {SideBar} from "./shared/header/SideBar";
import {Header} from './shared/header/Header'
import {ApolloClient, ApolloProvider, InMemoryCache} from '@apollo/client';
import type {User} from './graphql/graphql';
import {GET_CURRENT_USER} from './graphql/users.graphql';
import {UserSettingsProvider, useUserSettings} from './shared/UserSettingsProvider';
import {PageSpinner} from './shared/PageSpinner';
import {setContext} from '@apollo/client/link/context';
import {createHttpLink} from '@apollo/client/link/http';
import {LoggedOut} from './components/logged-out/LoggedOut'
import {PODContainer} from "./pages/PODContainer";
import {SnackbarContext, SnackBarContextProvider} from "./shared/providers/SnackbarContext";
import {ApplicationSnackbar} from "./shared/providers/ApplicationSnackbar";

const httpLink = createHttpLink({
    uri: process.env.REACT_APP_APPSYNC_FLOW_OVERVIEW_ENDPOINT!,
});

const authLink = setContext(async (_, {headers}) => {
    // Get the current token dynamically, renew if needed
    try {
        const tokenManager = oktaAuth.tokenManager;
        const accessToken = await tokenManager.get('accessToken');

        // If no token or expired, try to renew
        if (!accessToken || (accessToken.expiresAt && accessToken.expiresAt * 1000 < Date.now())) {
            await oktaAuth.tokenManager.renew('accessToken');
            const renewedTokenString = oktaAuth.getAccessToken();
            return {
                headers: {
                    ...headers,
                    authorization: renewedTokenString || "",
                }
            };
        }

        const tokenString = oktaAuth.getAccessToken();
        return {
            headers: {
                ...headers,
                authorization: tokenString || "",
            }
        };
    } catch (error) {
        console.error('Error getting/renewing access token:', error);
        return {
            headers: {
                ...headers,
                authorization: "",
            }
        };
    }
});

const E2E_AUTH_BYPASS = process.env.REACT_APP_E2E_AUTH_BYPASS === '1';

// Seed minimal Okta token storage early for bypass so okta-react sees authenticated state
if (E2E_AUTH_BYPASS) {
    try {
        const future = Math.floor(Date.now() / 1000) + 3600;
        const tokenPayload = {
            idToken: {idToken: 'dummy-id', claims: {sub: 'U1', name: 'Jane Doe', email: 'jane@example.com'}},
            accessToken: {accessToken: 'dummy-access', expiresAt: future}
        };
        window.localStorage.setItem('okta-token-storage', JSON.stringify(tokenPayload));
    } catch { /* noop */
    }
}

const cache = new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    exception: {
                        merge(existing, incoming) {
                            // Merge the ExceptionQuery fields together instead of replacing
                            return {
                                ...existing,
                                ...incoming
                            };
                        }
                    },
                    equipmentVehicle: {
                        merge(existing, incoming) {
                            // Merge the EquipmentVehicleQuery fields together instead of replacing
                            return {
                                ...existing,
                                ...incoming
                            };
                        }
                    },
                    user: {
                        merge(existing, incoming) {
                            // Merge the UserQuery fields together instead of replacing
                            return {
                                ...existing,
                                ...incoming
                            };
                        }
                    },
                    shipment: {
                        merge(existing, incoming) {
                            // Merge the ShipmentQuery fields together instead of replacing
                            return {
                                ...existing,
                                ...incoming
                            };
                        }
                    }
                }
            },
            ExceptionQuery: {
                fields: {
                    listCountries: {
                        keyArgs: [],
                        merge(existing, incoming) {
                            return incoming;
                        }
                    },
                    types: {
                        keyArgs: [],
                        merge(existing, incoming) {
                            return incoming;
                        }
                    }
                }
            },
            EquipmentVehicleQuery: {
                fields: {
                    listCountries: {
                        keyArgs: [],
                        merge(existing, incoming) {
                            return incoming;
                        }
                    },
                    list: {
                        keyArgs: ["customsTerritories", "filter"], // Cache separately by these arguments
                        merge(existing, incoming) {
                            return incoming;
                        }
                    }
                }
            },
            User: {
                fields: {
                    settings: {
                        merge(existing, incoming) {
                            // Always replace with incoming settings
                            return incoming;
                        }
                    }
                }
            },
            UserQuery: {
                fields: {
                    me: {
                        merge(existing, incoming) {
                            return incoming;
                        }
                    },
                    list: {
                        keyArgs: ["customsTerritory", "limit"], // Cache separately by these arguments
                        merge(existing, incoming) {
                            return incoming;
                        }
                    }
                }
            },
            ExceptionTypes: {
                keyFields: ["code"]
            },
            ShipmentQuery: {
                fields: {
                    list: {
                        keyArgs: ["consignmentId", "limit", "offset"], // cache by these args
                        merge(existing, incoming) {
                            return incoming;
                        }
                    },
                    listProducts: {
                        keyArgs: ["consignmentId", "limit", "offset", "shipmentNbr"], // cache by these args
                        merge(existing, incoming) {
                            return incoming;
                        }
                    }
                }
            }
        }
    })
;

const link = authLink.concat(httpLink);

const client = new ApolloClient({
    link,
    cache,
    headers: {
        authorization: oktaAuth.getAccessToken() || ''
    }
});

if (E2E_AUTH_BYPASS) {
    try {
        cache.writeQuery({
            query: GET_CURRENT_USER,
            data: {
                user: {
                    __typename: 'UserQuery',
                    me: {
                        __typename: 'User',
                        id: 'U1',
                        name: 'Jane Doe',
                        email: 'jane@example.com',
                        avatarUrl: null,
                        settings: {
                            __typename: 'UserSettings',
                            selectedTerritories: ['US'],
                            availableTerritories: ['US'],
                            uiSettings: JSON.stringify({})
                        }
                    }
                }
            }
        });
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[E2E_AUTH_BYPASS] Failed to seed current user', e);
    }
}


const history = createBrowserHistory()
const verticalNavStyle = {
    height: "100dvh"
}
type AppInSecureContextProps = {
    globalSearch: string;
    setGlobalSearch: (value: string) => void;
    debouncedGlobalSearch: string;
};

const HomeRedirect = () => {
    return <Navigate to="/exceptions"/>
}

const appRoutes = (user: User | undefined, stableTerritories: string[], globalSearch: string, setGlobalSearch: (v: string) => void, debouncedGlobalSearch: string) => [
    <Route path="/" element={<HomeRedirect/>} key="/"/>,
    <Route path="/dashboard/*" element={<Dashboard history={history}/>} key="/dashboard"/>,
    <Route path="/exceptions/:id?" element={<Exceptions history={history} currentUser={user} currentTerritories={stableTerritories}/>} key="/exceptions"/>,
    <Route path="/master-data/*" element={<MasterData history={history}/>} key="/master-data"/>,
    <Route path="/transport-vehicles/:id?" element={<EquipmentVehicles history={history} currentUser={user} currentTerritories={stableTerritories}/>} key="/transport-vehicles"/>,
    <Route path="/search/*" element={<SearchPage history={history} currentUser={user} currentTerritories={stableTerritories} globalSearch={globalSearch} setGlobalSearch={setGlobalSearch} debouncedGlobalSearch={debouncedGlobalSearch}/>}
           key="/search"/>,
    <Route path="/consignment/:id?" element={<ConsignmentPage history={history} currentUser={user} currentTerritories={stableTerritories}/>} key="/consignment"/>,
    <Route path="/pod/:tab?" element={<PODContainer history={history} currentUser={user} currentTerritories={stableTerritories}/>} key="/pod/"/>
];

const AppInSecureContext: React.FC<AppInSecureContextProps> = ({globalSearch, setGlobalSearch, debouncedGlobalSearch}) => {

    const {user, currentTerritories, loading} = useUserSettings();
    const snackbarCtx = useContext(SnackbarContext);

    const stableTerritories = React.useMemo(() => currentTerritories, [currentTerritories]);
    if (loading) return <PageSpinner/>;
    return <>
        <Routes>
            {E2E_AUTH_BYPASS
                ? appRoutes(user, stableTerritories, globalSearch, setGlobalSearch, debouncedGlobalSearch)
                : <Route element={<RequiredAuth/>}>
                    {appRoutes(user, stableTerritories, globalSearch, setGlobalSearch, debouncedGlobalSearch)}
                </Route>
            }
            <Route path="/implicit/callback" element={<LoginCallback errorComponent={LoginError}/>}/>
            <Route path="/logged-out" element={<LoggedOut/>}/>
        </Routes>
        {snackbarCtx.isDisplayed && <ApplicationSnackbar/>}
    </>
}

const App = () => {
    const navigate = useNavigate();

    const restoreOriginalUri = useCallback(
        (_oktaAuth: any, originalUri: string) => {
            navigate(toRelativeUrl(originalUri || '/', window.location.origin), {replace: true});
        },
        [navigate]
    );
    const [searchParams, setSearchParams] = useSearchParams();

    // Initialize globalSearch from the URL search param
    const initialSearch = searchParams.get('search') || '';
    const [globalSearch, setGlobalSearch] = React.useState<string>(initialSearch);

    function useDebounce(value: string, delay: number): string {
        const [debouncedValue, setDebouncedValue] = React.useState(value);
        React.useEffect(() => {
            const handler = setTimeout(() => setDebouncedValue(value), delay);
            return () => clearTimeout(handler);
        }, [value, delay]);
        return debouncedValue;
    }

    const debouncedGlobalSearch = useDebounce(globalSearch, 300);

    // Keep globalSearch and the URL in sync
    React.useEffect(() => {
        if (globalSearch) {
            searchParams.set('search', globalSearch);
            setSearchParams(searchParams, {replace: true});
        } else {
            searchParams.delete('search');
            setSearchParams(searchParams, {replace: true});
        }
    }, [globalSearch, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const updateBodyClass = () => {
            if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
                document.body.classList.add('eds--dark');
            } else {
                document.body.classList.remove('eds--dark');
            }
        };
        updateBodyClass();
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        mediaQuery.addEventListener('change', updateBodyClass);
        return () => {
            mediaQuery.removeEventListener('change', updateBodyClass);
            document.body.classList.remove('eds--dark');
        };
    }, []);

    const appShell = (
        <ApolloProvider client={client}>
            <UserSettingsProvider oktaAuth={oktaAuth}>
                <SnackBarContextProvider>
                    <div className="eds-flex" style={verticalNavStyle}>
                        <SideBar/>
                        <div className="eds-flex--grow-1">
                            <Header isDark={false}/>
                            <div className="scrollable">
                                <AppInSecureContext
                                    globalSearch={globalSearch}
                                    setGlobalSearch={setGlobalSearch}
                                    debouncedGlobalSearch={debouncedGlobalSearch}
                                />
                            </div>
                        </div>
                    </div>
                </SnackBarContextProvider>
            </UserSettingsProvider>
        </ApolloProvider>
    );

    // Always mount Security to keep Okta React context available even in bypass mode.
    // Bypass mode seeds tokens so authentication resolves immediately.
    return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            {appShell}
        </Security>
    );
};

export default App
